import { useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import { useData } from "../useData";
// Calendar events are public within the org — all reps see all calls
import { computeDateRange } from "../../utils/calendarUtils";
import {
  getCache,
  setCache,
  getEventsCacheKey,
  EVENTS_CACHE_TTL,
} from "../../lib/calendarCacheService";
import type { CalendarEvent } from "../../types/calendar";

// =============================================================================
// useCalendarPrefetch - BACKGROUND CALENDAR DATA PREFETCHER
// =============================================================================
// PURPOSE: Silently pre-loads current week's calendar data on app mount,
// so the Calendar page renders instantly when the user navigates to it.
//
// REQUIREMENTS:
// - Runs on any admin page (called from AppLayout)
// - Fire-and-forget: no loading states, no error UI
// - Skips if cache already warm (no redundant fetches)
// - Re-runs on org switch (new org = new data)
// - Uses AbortController for cleanup on unmount
//
// KEY DECISIONS:
// - Prefetches "week" view centered on today (the default calendar view)
// - Warms EVENTS ONLY (same query + cache key as useCalendarData). The
//   enrichment warm was deleted Jun 13 2026 — it wrote a stale v2 shape the
//   reader rejected on every load (see comment at the bottom of prefetch()).
// =============================================================================

export function useCalendarPrefetch(): void {
  const { currentOrganization } = useData();
  const orgId = currentOrganization?.id ?? null;
  const timezone = currentOrganization?.timezone || "UTC";
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;

    if (!supabase || !orgId) return;
    const db = supabase; // Capture for TypeScript narrowing in async closure

    const prefetch = async () => {
      try {
        // Compute current week range (same as CalendarPage default)
        const { rangeStart, rangeEnd } = computeDateRange(new Date(), "week", timezone);
        const startISO = rangeStart.toISOString();
        const endISO = rangeEnd.toISOString();

        // Check if events are already cached — skip if warm
        const eventsCacheKey = getEventsCacheKey(orgId, startISO, endISO);
        if (getCache<CalendarEvent[]>(eventsCacheKey, EVENTS_CACHE_TTL, orgId)) {
          return; // Already warm, nothing to do
        }

        // Query 1: Raw events (same as useCalendarData)
        const eventsQuery = db
          .from("transactions")
          .select(
            `
            id,
            call_start_time,
            call_booked_time,
            event_date,
            event_type,
            status,
            lead_id,
            closer_id,
            setter_id,
            call_cancel_by,
            call_cancel_reason,
            organization_id,
            leads:lead_id ( name, email ),
            closer:closer_id ( full_name ),
            setter:setter_id ( full_name )
          `
          )
          .eq("organization_id", orgId)
          .in("event_type", ["call_booked", "call_cancelled"])
          .not("call_start_time", "is", null)
          .gte("call_start_time", startISO)
          .lte("call_start_time", endISO)
          .order("call_start_time", { ascending: true })
          .limit(1000);

        const { data: rawData } = await eventsQuery;

        if (abortRef.current) return;

        // Map and deduplicate (same logic as useCalendarData)
        const mapped: CalendarEvent[] = (rawData || []).map((row: any) => ({
          id: row.id,
          call_start_time: row.call_start_time,
          call_booked_time: row.call_booked_time,
          event_date: row.event_date,
          event_type: row.event_type,
          status: row.status,
          lead_id: row.lead_id,
          lead_name: row.leads?.name || null,
          lead_email: row.leads?.email || null,
          closer_id: row.closer_id,
          closer_name: row.closer?.full_name || null,
          setter_id: row.setter_id,
          setter_name: row.setter?.full_name || null,
          call_cancel_by: row.call_cancel_by,
          call_cancel_reason: row.call_cancel_reason,
          organization_id: row.organization_id,
        }));

        // Deduplicate: remove call_booked if call_cancelled exists for same lead+time
        const cancelledKeys = new Set<string>();
        for (const ev of mapped) {
          if (ev.event_type === "call_cancelled" && ev.lead_id) {
            cancelledKeys.add(`${ev.lead_id}|${ev.call_start_time}`);
          }
        }
        const deduplicated = mapped.filter((ev) => {
          if (ev.event_type === "call_booked" && ev.lead_id) {
            return !cancelledKeys.has(`${ev.lead_id}|${ev.call_start_time}`);
          }
          return true;
        });

        setCache(eventsCacheKey, deduplicated, orgId);

        // NO enrichment prefetch (P0.5-CACHE, Jun 13 2026). The old write here
        // stored the v2 shape `{preCallLeadIds, outcomes}`, but the reader
        // (useCalendarEnrichment) requires v3 `{preCallLeadIds, bindings,
        // leadCount}` and its `Array.isArray(cached.bindings)` guard rejected
        // the v2 entry on EVERY app load — 2 of this hook's 3 queries warmed
        // nothing. Mirroring v3 would mean replicating computeOutcomeBindings
        // here; not worth it for a 3-min-TTL cache. Don't re-add a write
        // without matching the reader's CURRENT CachedEnrichment shape.
        logger.info("Calendar prefetch complete: current week events cached");
      } catch (err) {
        // Silent failure — prefetch is best-effort
        logger.warn("Calendar prefetch failed (non-critical):", err);
      }
    };

    prefetch();

    return () => {
      abortRef.current = true;
    };
  }, [orgId, timezone]);
}
